import React, { useContext, useRef } from 'react';
import { useDrag } from 'react-dnd';
import { ColumnProps } from 'antd/lib/table';

import AddressContext from '@totem/components/orgManagement/orgDetails/buildings/options/addressContext';
import { NetSuiteAddressRecord } from '@totem/components/orgManagement/orgDetails/netSuiteKeys/types';
import IBTable from '@totem/components/ui_components/antDesignOverrides/IBTable';
import { isNotNull } from '@totem/utilities/common';
import { sortStringAscending } from '@totem/utilities/tableUtilities';

const AddressTable = () => {
  const { netSuiteAddresses, pageSize } = useContext(AddressContext);
  const DND_ITEM_TYPE = 'NETSUITE_ADDRESS';

  type DragItem = {
    netSuiteAddressKey: string;
  };

  type DragRowProps = React.HTMLAttributes<HTMLTableRowElement> & {
    index?: number;
    record?: NetSuiteAddressRecord;
  };

  const DraggableAddressRow = ({
    index,
    record,
    className,
    style,
    ...restProps
  }: DragRowProps) => {
    const ref = useRef<HTMLTableRowElement>(null);

    const [{ isDragging }, drag] = useDrag({
      type: DND_ITEM_TYPE,
      item: { netSuiteAddressKey: record?.key } as DragItem,
      canDrag: !!record?.key,
      collect: (monitor) => ({
        isDragging: monitor.isDragging(),
      }),
    });

    drag(ref);

    return (
      <tr
        {...restProps}
        ref={ref}
        className={className}
        style={{
          cursor: 'grab',
          opacity: isDragging ? 0.4 : 1,
          ...style,
        }}
        data-row-index={index}
      />
    );
  };

  const columns: ColumnProps<NetSuiteAddressRecord>[] = [
    {
      title: 'NetSuite Key',
      dataIndex: 'netSuiteKey',
      key: 'netSuiteKey',
      render: (_, record: NetSuiteAddressRecord) => <span>{record.key}</span>,
      defaultSortOrder: 'ascend',
      sorter: (compA, compB) => sortStringAscending(compA.key, compB.key),
    },
    {
      title: 'Address',
      dataIndex: 'address',
      key: 'company',
      render: (_, record: NetSuiteAddressRecord) => (
        <pre>{record.address.addrText}</pre>
      ),
      defaultSortOrder: 'ascend',
      sorter: (compA, compB) =>
        sortStringAscending(compA.address.addrText, compB.address.addrText),
    },
  ];

  return (
    <>
      <IBTable
        showSorterTooltip
        columns={columns}
        dataSource={isNotNull(netSuiteAddresses) ? netSuiteAddresses : []}
        loading={false}
        rowKey={(record) => record.key}
        components={{
          body: {
            row: DraggableAddressRow,
          },
        }}
        onRow={(record, index) =>
          ({
            index,
            record,
          }) as React.HTMLAttributes<HTMLElement>
        }
        pagination={{
          showSizeChanger: true,
          pageSize,
        }}
      />
    </>
  );
};

export default AddressTable;
